import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import SidebarLayout from '../common/SidebarLayout.js';
import Dashboard from './Dashboard.js';
import Sales from './Sales.js';
import Products from './Products.js';
import Inventory from './Inventory.js';
import Expenses from './Expenses.js';
import BranchSetup from './BranchSetup.js';
import Orders from './Orders.js';
import BranchWaiters from './BranchWaiters.js';
import { useOrderStore } from '../../store/branchstore/branchOrdersStore.js';
import useAppStore from '../../store/useAppStore.js';
import { fetchInventory, fetchProducts } from '../../services/inventoryApi.js';
import * as offlineDb from '../../services/offlineDb.js';
import socketConfig from '../../config/socketconfig.js';

const menuItems = [
  { label: 'Dashboard', icon: '📊' },
  { label: 'Sales', icon: '💰' },
  { label: 'Orders', icon: '📋' },
  { label: 'Products', icon: '📦' },
  { label: 'Inventory', icon: '📋' },
  { label: 'Expenses', icon: '🧾' },
  { label: 'Branch Setup', icon: '⚙️' }
];

const BranchManagerLayout = () => {
  const [activeMenu, setActiveMenu] = useState(() => {
    const savedMenu = localStorage.getItem('branchManagerActiveMenu');
    return savedMenu || 'Sales';
  });
  const socketRef = useRef(null);
  const { updateOrderStatus } = useOrderStore((state) => ({
    updateOrderStatus: state.updateOrderStatus,
  }));
  const { initializeInventory, initializeProducts, setPendingOrders, selectedBranch, loadBranches, fetchBranchReceiptData, setProducts, setProductCategories, offlineMode, initializeOfflineState } = useAppStore((state) => ({
    initializeInventory: state.initializeInventory,
    initializeProducts: state.initializeProducts,
    setPendingOrders: state.setPendingOrders,
    selectedBranch: state.selectedBranch,
    loadBranches: state.loadBranches,
    fetchBranchReceiptData: state.fetchBranchReceiptData,
    setProducts: state.setProducts,
    setProductCategories: state.setProductCategories,
    offlineMode: state.offlineMode,
    initializeOfflineState: state.initializeOfflineState
  }));

  useEffect(() => {
    if (offlineMode) {
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) return;

    const socketUrl = socketConfig.SOCKET_URL;
    socketRef.current = io(socketUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      auth: { token }
    });

    socketRef.current.on('connect_error', (error) => {
      console.error('BranchManagerLayout socket connection error:', error);
    });

    socketRef.current.on('order_updated', (data) => {
      // Handle order_updated event with structure: {order_id, status, updated_at}
      const orderId = data.order_id ? Number(data.order_id) : data.order?.id;
      if (orderId) {
        updateOrderStatus(orderId, {
          status: data.status,
          updated_at: data.updated_at
        });
      } else if (data.order && data.order.id) {
        updateOrderStatus(data.order.id, data.order);
      }

      if (data.status === 'completed' || data.status === 'cancelled') {
        setPendingOrders((prev) => prev.filter((order) => order.id !== orderId));
      }
    });

    socketRef.current.on('token_event', (data) => {
      const orderId = data.order_id || data.order?.id || data.order?.order_id;
      const status = data.event?.status || data.status;

      if (status === 'completed' || status === 'cancelled') {
        setPendingOrders((prev) => prev.filter((order) => order.id !== orderId));
      }
    });

    socketRef.current.on('token_issued', (data) => {
      const newToken = data.token || { token_no: data.token_id };
      const orderId = data.order_id || data.order?.id;
      if (!orderId) return;

      setPendingOrders((prev) => {
        const exists = prev.some((order) => order.id === orderId);
        if (exists) return prev;

        return [
          {
            id: orderId,
            token: newToken.token_no || newToken.token_id || `#${orderId}`,
            orderTime: data.created_at ? new Date(data.created_at) : null,
            table: data.table_name || (data.table_id ? `Table ${data.table_id}` : 'Unknown'),
            items: (data.items || []).map((item) => ({
              name: item.name,
              quantity: item.quantity
            })),
          },
          ...prev,
        ];
      });
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [offlineMode, updateOrderStatus, setPendingOrders]);

  useEffect(() => {
    localStorage.setItem('branchManagerActiveMenu', activeMenu);
  }, [activeMenu]);

  useEffect(() => {
    const loadBranchData = async () => {
      try {
        await initializeOfflineState?.();

        if (!selectedBranch) {
          await loadBranches();
        }

        if (offlineMode) {
          const products = await offlineDb.getOfflineProducts();
          const categories = await offlineDb.getOfflineCategories();
          setProducts(products || []);
          setProductCategories((categories || []).map((category) => ({
            label: category.name || category.value || category.label || 'Unknown',
            value: category.name || category.value || category.label || 'Unknown'
          })));
          return;
        }

        await Promise.all([
          initializeInventory(fetchInventory),
          initializeProducts(fetchProducts)
        ]);
      } catch (error) {
        console.error('Error preloading branch data:', error);
      }
    };

    loadBranchData();
  }, [initializeInventory, initializeProducts, initializeOfflineState, loadBranches, selectedBranch, offlineMode, setProducts, setProductCategories]);

  useEffect(() => {
    if (!selectedBranch?.id) return;

    const normalizedBranchId = String(selectedBranch.id).replace(/^BR-/, '');
    fetchBranchReceiptData(normalizedBranchId);
  }, [selectedBranch, fetchBranchReceiptData]);

  const renderContent = () => {
    switch (activeMenu) {
      case 'Dashboard':
        return <Dashboard />;
      case 'Sales':
        return <Sales />;
      case 'Orders':
        return <Orders onEditOrder={() => setActiveMenu('Sales')} />;
      case 'Waiters':
        return <BranchWaiters />;
      case 'Products':
        return <Products />;
      case 'Inventory':
        return <Inventory />;
      case 'Expenses':
        return <Expenses />;
      case 'Branch Setup':
        return <BranchSetup />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <SidebarLayout
      title="Branch Manager"
      subtitle="Manage branch operations"
      menuItems={menuItems}
      activeItem={activeMenu}
      onMenuClick={setActiveMenu}
      userRole="Branch Manager"
      hideHeader={activeMenu === 'Sales'}
    >
      {renderContent()}
    </SidebarLayout>
  );
};

export default BranchManagerLayout;